/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React, { useState, createRef, useEffect } from 'react';
import {
    object, shape, string, func, bool,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Modal } from '@material-ui/core';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';
import Helmet from 'react-helmet';
import { addCGCLabelsDataToApi } from '../../../../../state/ducks/CGC/CGC-Actions';
import { getGCIPersonalizationKey, getCustomCardData } from '../../../../../state/ducks/App/App-Selectors';
import SelectCelebrationsCustomsCard from './SelectCustoms/SelectCelebrationsCustomsCard';
import PersonalizeCustoms from './PersonalizeCustoms/PersonalizeCustoms';
import { base64StringtoFile, extractImageFileExtensionFromBase64 } from './ImageCropUtils';
import CGCModalHeader from '../CGCModalHeader';
import CelebrationsCustomModalNav from './CelebrationsCustomModalNav';
import ReviewCustom from './ReviewCustom/ReviewCustom';
import findContent from '../../../../gql/queries/findContent';
import { DefaultTools } from '../RTE/Config/Default';
import GraphqlPortalLoader from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlPortalLoader';

const styles = (theme) => ({
    title: {
        fontSize: 28,
        textAlign: 'center',
        padding: '24px 0px',
    },
    container: {
        position: 'absolute',
        top: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: 1280,
        width: '80%',
        background: theme.palette.cgc?.bgColor || 'white',
        '&:focus': {
            outline: 'none',
        },
        [theme.breakpoints.down('xl')]: {
            width: '90%',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            maxHeight: '100%',
            minHeight: 570,
            top: 0,
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: '100%',
            maxHeight: '100%',
            minHeight: 430,
            top: 10,
        },
    },
});
const MODAL_VIEWS = {
    SELECT_TEMPLATE: 'select',
    PERSONALIZE: 'personalize',
    REVIEW: 'review',
};
const CelebrationsCustomsCardView = ({
    customData, classes, closeModal, updateCGCCustomsData, gciKey,
    cartItemId, addCGCData, customType, openModal, customCardData,
    existingData, setCGCLabelData,
}) => {
    const [customsCardType, setCustomsCardType] = useState('');
    const [customView, setCustomView] = useState(MODAL_VIEWS.SELECT_TEMPLATE);
    const [fileName, setFileName] = useState('');
    const [fileImage, setFileImage] = useState('');
    const [image, setImage] = useState('');
    const [imageExt, setImageExt] = useState('jpg');
    const [message, setMessage] = useState('');
    const [croppedImage, setcroppedImage] = useState('');
    const imagePreviewCanvasRef = createRef(null);
    const customsCard = customData?.label_image?.url;
    const customCardStyling = customData?.message_image_settings;
    // add CGC Customs data to api and redux

    const resetState = () => {
        setCustomView(MODAL_VIEWS.SELECT_TEMPLATE);
        setCustomsCardType('');
        setImage('');
        setImageExt('');
        setMessage({});
    };

    useEffect(() => {
        if (existingData && Object.keys(existingData).length) {
            const messageData = {
                text: existingData.message_body,
                style: {
                    fontSize: existingData.font_size,
                    fontFamily: existingData.font_family,
                    color: existingData.text_color,
                    textAlign: existingData.text_alignment,
                },
            };
            setCustomView(MODAL_VIEWS.PERSONALIZE);
            setCustomsCardType(existingData.label_type);
            setImage(existingData.cover_image ? `data:${existingData.cover_image_mimes};base64,${existingData.cover_image}` : '');
            setImageExt(existingData.cover_image_mimes);
            setMessage(messageData);
        }
    }, [existingData]);

    useEffect(() => {
        if (typeof customCardData === 'object' && !customCardData?.thumbnail) {
            resetState();
        }
    }, [customCardData]);

    const addCustomsCard = () => {
        if (image !== '' || message?.text) {
            const data = {
                text: message.text,
                thumbnail: customsCard,
                coverImage: image,
                action: 'personalizationComplete',
                fileImage,
            };

            const imageConverted = croppedImage;
            if (updateCGCCustomsData) {
                updateCGCCustomsData({ sku: customData.sku, key_id: gciKey, ...data  });
            }

            // customType is for labels or hangtags and customCardType is for photo, message or both,
            addCGCData({
                key_id: gciKey,
                cardData: customData,
                message,
                imageConverted,
                cgcData: { cartItemId },
                customsCardType,
                customType,
                fileImage,
                setCGCLabelData,
                updateCGCCustomsData,
            });
            closeModal();
        }
    };

    const handleImage = (e) => {
        const currentFile = e.target.files[0];
        setFileName(e.target.files[0]?.name);

        const myFileItemReader = new FileReader();
        myFileItemReader.addEventListener('load', () => {
            const myResult = myFileItemReader.result;
            const imgExt = extractImageFileExtensionFromBase64(myResult);
            if (imgExt === 'jpg' || imgExt === 'jpeg' || imgExt === 'png') {
                setImage(myResult);
                setImageExt(imgExt);
            }
        }, false);

        myFileItemReader.readAsDataURL(currentFile);
    };
    const handleCustom = (type) => {
        setCustomsCardType(type);
        setCustomView(MODAL_VIEWS.PERSONALIZE);
    };
    const handlePersonalize =  () => {
        const canvasRef = imagePreviewCanvasRef.current;
        const imageData64 = canvasRef?.toDataURL(`image/${imageExt}`);

        if (fileName && imageData64) {
            const fileImageResult = base64StringtoFile(imageData64, fileName);

            const formData = new FormData();
            formData.append('file', fileImageResult);

            setFileImage(formData);
        }
        setcroppedImage(imageData64);
        setCustomView(MODAL_VIEWS.REVIEW);
    };
    const FONT_QUERY = findContent({
        brand: 'gci',
        contentType: 'fonts',
    });
    const { data, error, loading } = useQuery(FONT_QUERY);
    let response = null;
    if (loading) {
        return  <GraphqlPortalLoader />;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: FONT_QUERY,
            component: 'PersonalizeCustoms.js',
            message: 'Failed to fetch content data for: content type fonts, on brand gci',
            error,
        });
        return null;
    }

    const toolsFontData = [];
    const fonts = [];
    if (data) {
        const toolsData = data?.findContent?.content?.entries;
        DefaultTools.forEach((tool) => {
            if (tool.value === 'font') {
                const fontData =  toolsData?.map((font, index) => {
                    fonts.push(
                        <style
                            /* eslint-disable react/no-array-index-key */
                            key={`font-${index}`}
                        >
                            {`@font-face {font-family: ${font.family_name}; src: url('${font?.font_path?.href}'); font-display: swap;`}
                        </style>,
                    );
                    return { maxChar: font.max_char_cards, label: font.title, value: font.family_name };
                });
                toolsFontData.push({ ...tool, list: fontData });
            } else if (toolsFontData.filter((toolData) => toolData?.value === tool?.value).length === 0) {
                toolsFontData.push({ ...tool });
            }
        });
    }

    response = (
        <Modal open={openModal}>
            <Grid container className={classes.container}>
                {fonts?.length > 0 && <Helmet>{fonts}</Helmet>}
                <CGCModalHeader
                    closeModal={closeModal}
                    headerTitle="Celebration&apos;s Customs"
                />
                <CelebrationsCustomModalNav
                    modalViews={MODAL_VIEWS}
                    selectedView={customView}
                    selectedCard={customsCard}
                    messageData={message}
                    setCustomView={setCustomView}
                    customType={customType}
                    handlePersonalize={handlePersonalize}
                />
                <Grid container>
                    {MODAL_VIEWS.SELECT_TEMPLATE === customView
                    &&                            (
                        <SelectCelebrationsCustomsCard
                            customData={customData}
                            customsCard={customsCard}
                            setCustomsCardType={handleCustom}
                        />
                    )}
                    {MODAL_VIEWS.PERSONALIZE === customView
                    &&                        (
                        <PersonalizeCustoms
                            customData={customData}
                            customsCard={customsCard}
                            customCardType={customsCardType}
                            image={image}
                            message={message}
                            setImage={setImage}
                            setMessage={setMessage}
                            personalizeCustomsCard={handlePersonalize}
                            handleImage={handleImage}
                            customCardStyling={customCardStyling}
                            imagePreviewCanvasRef={imagePreviewCanvasRef}
                            customType={customType}
                            toolsFontData={toolsFontData}
                        />
                    )}
                    {MODAL_VIEWS.REVIEW === customView
                    && (
                        <ReviewCustom
                            customData={customData}
                            customsCard={customsCard}
                            customsCardType={customsCardType}
                            message={message}
                            croppedImage={croppedImage}
                            addCustomsCard={addCustomsCard}
                            customType={customType}
                        />
                    )}
                </Grid>
            </Grid>
        </Modal>
    );
    return response;
};
CelebrationsCustomsCardView.propTypes = {
    classes: object.isRequired,
    customData: shape({
        label_image: shape({
            url: string.isRequired,
        }),
    }),
    cartItemId: string.isRequired,
    gciKey: string,
    closeModal: func.isRequired,
    updateCGCCustomsData: func.isRequired,
    addCGCData: func.isRequired,
    customType: string.isRequired,
    openModal: bool.isRequired,
    customCardData: shape({
        thumbnail: string,
    }),
    existingData: shape({
        cover_image: string,
        message_body: string,
        label_type: string,
    }),
    setCGCLabelData: func.isRequired,
};

CelebrationsCustomsCardView.defaultProps = {
    customData: {},
    gciKey: '',
    existingData: {},
    customCardData: {},
};
const mapStateToProps = (state) => ({
    gciKey: getGCIPersonalizationKey(state),
    existingData: getCustomCardData(state),
});
const mapDispatchToProps = (dispatch) => ({
    addCGCData: bindActionCreators(addCGCLabelsDataToApi, dispatch),
});
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CelebrationsCustomsCardView));
